import React, { useState, useRef, useCallback } from 'react';
import { localestr } from '../utils.js';
import icon from '../icons.svg';
import './Checker.css';
import PropTypes from 'prop-types';

function Checker({ caption, data, setData }) {
  const [curr, setCurr] = useState(0);
  const targetRef = useRef(null);

  const handleDragStart = useCallback((idx) => {
    targetRef.current = idx;
  }, []);

  const handleDragLeave = useCallback((e) => {
    e.target.style.background = '';
  }, []);

  const handleDragEnter = useCallback((e) => {
    e.target.style.background = '#92929233';
  }, []);

  const handleDragOver = useCallback((e) => {
    e.preventDefault();
  }, []);

  const handleDrop = useCallback(
    (e, idx) => {
      e.preventDefault();
      e.stopPropagation();
      e.target.style.background = '';
      const startIdx = targetRef.current;
      if (typeof startIdx !== 'number' || data[startIdx]?.disable_move || data[idx]?.disable_move) return;

      const newData = [...data];
      const [movedItem] = newData.splice(startIdx, 1);
      newData.splice(idx, 0, movedItem);
      setData(newData);
      setCurr(idx);
    },
    [data, setData]
  );

  const handleChange = useCallback(
    (idx) => {
      const newData = [...data];
      newData[idx] = { ...newData[idx], checked: !newData[idx].checked };
      setData(newData);
    },
    [data, setData]
  );

  const moveItem = useCallback(
    (direction = 1) => {
      if (curr <= 0 && direction < 0) return;
      if (curr >= data.length - 1 && direction > 0) return;

      const newData = [...data];
      const targetIdx = curr + direction;
      if (newData[curr]?.disable_move || newData[targetIdx]?.disable_move) return;

      [newData[curr], newData[targetIdx]] = [newData[targetIdx], newData[curr]];
      setData(newData);
      setCurr(targetIdx);
    },
    [curr, data, setData]
  );

  const checkAll = useCallback(
    (checked = true) => {
      const newData = data.map((el) => ({ ...el, checked }));
      setData(newData);
    },
    [data, setData]
  );

  const handleKeyPress = useCallback(
    (e) => {
      switch (e.key) {
        case 'ArrowUp':
          if (curr > 0) setCurr(curr - 1);
          break;
        case 'ArrowDown':
          if (curr < data.length - 1) setCurr(curr + 1);
          break;
        case 'Home':
          setCurr(0);
          break;
        case 'End':
          setCurr(data.length - 1);
          break;
        case ' ':
          e.preventDefault();
          handleChange(curr);
          break;
        default:
          break;
      }
    },
    [curr, data.length, handleChange]
  );

  const setFocus = useCallback((el) => {
    if (el && document.activeElement === document.body) {
      el.focus();
    }
  }, []);

  const items = data.map((el, idx) => {
    const isSelected = idx === curr;
    const commonProps = {
      className: `ch_item ${isSelected ? 'selected' : ''}`,
      onClick: () => setCurr(idx),
      onKeyUp: handleKeyPress,
    };

    const labelContent = (
      <label>
        <input
          type="checkbox"
          checked={el.checked}
          onChange={() => handleChange(idx)}
          ref={isSelected ? (e) => setFocus(e) : null}
        />
        {el.name}
      </label>
    );

    if (el.disable_move) {
      return (
        <div {...commonProps} key={idx}>
          {labelContent}
        </div>
      );
    }

    return (
      <div
        {...commonProps}
        key={idx}
        draggable
        onDragStart={() => handleDragStart(idx)}
        onDragLeave={handleDragLeave}
        onDragEnter={handleDragEnter}
        onDragOver={handleDragOver}
        onDrop={(e) => handleDrop(e, idx)}
      >
        {labelContent}
      </div>
    );
  });

  return (
    <fieldset className="legend">
      <legend>{caption}</legend>
      <div className="toolBar">
        <div
          tabIndex={0}
          className="toolBarItem"
          title={`${localestr('Відмітити')} (Ins)`}
          onClick={() => checkAll(true)}
          onKeyUp={(e) => e.key === 'Enter' && checkAll(true)}
          role="button"
          aria-label={localestr('Відмітити')}
        >
          <img src={`${icon}#checkBut`} alt={localestr('Відмітити')} />
        </div>
        <div
          tabIndex={0}
          className="toolBarItem"
          title={`${localestr('Зняти відмітки')} (Ins)`}
          onClick={() => checkAll(false)}
          onKeyUp={(e) => e.key === 'Enter' && checkAll(false)}
          role="button"
          aria-label={localestr('Зняти відмітки')}
        >
          <img src={`${icon}#unchckBut`} alt={localestr('Зняти відмітки')} />
        </div>
        <div
          tabIndex={0}
          className="toolBarItem"
          title={`${localestr('Вверх')} (Ins)`}
          onClick={() => moveItem(-1)}
          onKeyUp={(e) => e.key === 'Enter' && moveItem(-1)}
          role="button"
          aria-label={localestr('Вверх')}
        >
          <img src={`${icon}#upBut`} alt={localestr('Вверх')} />
        </div>
        <div
          tabIndex={0}
          className="toolBarItem"
          title={`${localestr('Вниз')} (Ins)`}
          onClick={() => moveItem(1)}
          onKeyUp={(e) => e.key === 'Enter' && moveItem(1)}
          role="button"
          aria-label={localestr('Вниз')}
        >
          <img src={`${icon}#dnBut`} alt={localestr('Вниз')} />
        </div>
      </div>
      <div role="listbox" onKeyUp={handleKeyPress}>
        {items}
      </div>
    </fieldset>
  );
}

Checker.propTypes = {
  caption: PropTypes.string.isRequired,
  data: PropTypes.arrayOf(
    PropTypes.shape({
      name: PropTypes.string.isRequired,
      checked: PropTypes.bool.isRequired,
      disable_move: PropTypes.bool,
    })
  ).isRequired,
  setData: PropTypes.func.isRequired,
};

export default Checker;